import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiHttpError } from '@/api/client';
import { usersApi } from '@/api/users';
import { useAuth } from '@/auth/AuthContext';
import { Skeleton } from '@/components/Skeleton';

interface Badge {
  key: string;
  icon: string;
  name: string;
  desc: string;
  unlocked: boolean;
}

// Static badge spec for v1. Real progress comes from the user's stats +
// a small client-side rules table. When the backend ships /v1/badges/me
// this collapses to "GET that, render".
function buildBadges(stats: {
  reportsCount: number;
  reviewsCount: number;
  stallsAddedCount: number;
}): Badge[] {
  return [
    {
      key: 'pioneer',
      icon: '🌶️',
      name: 'Pioneer',
      desc: 'First 1k users',
      unlocked: true,
    },
    {
      key: 'night-owl',
      icon: '🦉',
      name: 'Night Owl',
      desc: '5 late-night reports',
      unlocked: stats.reportsCount >= 5,
    },
    {
      key: 'noodle-pro',
      icon: '🍜',
      name: 'Noodle Pro',
      desc: `10 noodle votes · ${Math.min(stats.reportsCount, 10)}/10`,
      unlocked: stats.reportsCount >= 10,
    },
    {
      key: 'streetwise',
      icon: '🛵',
      name: 'Streetwise',
      desc: 'Vote in 5 neighborhoods',
      unlocked: stats.reportsCount >= 25,
    },
    {
      key: 'curator',
      icon: '🥘',
      name: 'Curator',
      desc: `3 stalls approved · ${Math.min(stats.stallsAddedCount, 3)}/3`,
      unlocked: stats.stallsAddedCount >= 3,
    },
    {
      key: 'reviewer',
      icon: '📝',
      name: 'Reviewer',
      desc: `25 reviews · ${Math.min(stats.reviewsCount, 25)}/25`,
      unlocked: stats.reviewsCount >= 25,
    },
  ];
}

export function ProfileRoute() {
  const { t } = useTranslation();
  const auth = useAuth();

  const meQuery = useQuery({
    queryKey: ['users', 'me'],
    queryFn: async ({ signal }) => {
      const token = await auth.getToken();
      if (!token) throw new Error('sign_in_required');
      return usersApi.getMe(token, signal);
    },
    enabled: Boolean(auth.user),
    retry: (count, e) => {
      if (e instanceof ApiHttpError && e.status < 500) return false;
      return count < 2;
    },
  });

  if (!auth.user) {
    return (
      <div className="mx-auto max-w-[480px] px-4 py-12 text-center">
        <div className="mb-3 text-5xl" aria-hidden="true">
          🎖️
        </div>
        <h1 className="m-0 mb-2 text-2xl font-bold">{t('nav.profile')}</h1>
        <p className="m-0 mb-4 text-sm text-ink-soft">{t('vote.sign_in_required')}</p>
        <Link
          to="/signin"
          className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-soi-600 px-4 py-2 text-sm font-semibold text-white"
        >
          {t('nav.signin')}
        </Link>
      </div>
    );
  }

  const { data, isLoading, isError, error } = meQuery;
  const displayName =
    data?.user.displayName ?? auth.user.displayName ?? auth.user.email ?? 'You';
  const initial = (displayName[0] ?? 'O').toUpperCase();
  const avatarUrl = data?.user.avatarUrl ?? auth.user.photoURL ?? null;
  const points = data?.user.points ?? 0;
  const stats = data?.stats ?? { reportsCount: 0, reviewsCount: 0, stallsAddedCount: 0 };
  const badges = buildBadges(stats);

  return (
    <div className="mx-auto max-w-[720px] px-4 pb-16 pt-6">
      <header className="flex items-center gap-4">
        <div
          className="grid h-20 w-20 place-items-center rounded-full text-3xl font-extrabold text-white shadow-s2"
          style={{ background: 'linear-gradient(135deg, var(--soi-400), var(--soi-700))' }}
          aria-hidden={!avatarUrl}
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="h-full w-full rounded-full object-cover" />
          ) : (
            initial
          )}
        </div>
        <div>
          <h1 className="m-0 mb-1 text-3xl font-bold">{displayName}</h1>
          <p className="m-0 text-sm text-ink-soft">{auth.user.email ?? '—'}</p>
        </div>
      </header>

      <section
        className="mt-6 flex items-center justify-between rounded-lg p-6 text-white"
        style={{ background: 'linear-gradient(135deg, var(--soi-600), var(--soi-400))' }}
      >
        <div>
          <div
            className="text-xs font-bold uppercase opacity-90"
            style={{ letterSpacing: '0.1em' }}
          >
            {t('profile.points')}
          </div>
          {isLoading ? (
            <Skeleton width={120} height={44} style={{ background: 'rgba(255,255,255,0.3)' }} />
          ) : isError ? (
            <div className="text-2xl font-extrabold">—</div>
          ) : (
            <div className="tabular text-[44px] font-extrabold leading-none">{points}</div>
          )}
        </div>
        <div className="text-right text-xs leading-relaxed opacity-90">
          <p className="m-0">{t('profile.next_badge_hint')}</p>
        </div>
      </section>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <Stat label={t('profile.stats.reports')} value={stats.reportsCount} loading={isLoading} />
        <Stat label={t('profile.stats.reviews')} value={stats.reviewsCount} loading={isLoading} />
        <Stat
          label={t('profile.stats.stalls_added')}
          value={stats.stallsAddedCount}
          loading={isLoading}
        />
      </div>

      <section className="mt-8">
        <h2 className="m-0 mb-3 text-xl font-bold">{t('profile.badges')}</h2>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(110px,1fr))] gap-3">
          {badges.map((b) => (
            <article
              key={b.key}
              className={[
                'rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] p-3 text-center',
                b.unlocked ? '' : 'opacity-45 grayscale',
              ].join(' ')}
            >
              <div className="mb-1.5 text-3xl" aria-hidden="true">
                {b.icon}
              </div>
              <div className="text-[13px] font-bold">{b.name}</div>
              <div className="mt-0.5 text-[11px] text-ink-muted">{b.desc}</div>
            </article>
          ))}
        </div>
        <p className="mt-3 text-[13px] text-ink-muted">{t('profile.no_leaderboard')}</p>
      </section>

      <section className="mt-8">
        <h2 className="m-0 mb-3 text-xl font-bold">{t('profile.data_account')}</h2>
        <div className="flex flex-wrap gap-3">
          <a
            href="/privacy"
            className="inline-flex min-h-[44px] items-center justify-center rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-2 text-sm font-semibold text-ink hover:bg-soi-100"
          >
            {t('profile.view_privacy')}
          </a>
          <button
            type="button"
            onClick={() => void auth.signOut()}
            className="inline-flex min-h-[44px] items-center justify-center rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-2 text-sm font-semibold text-ink hover:bg-soi-100"
          >
            {t('nav.signout')}
          </button>
          <a
            href="/delete-account"
            className="inline-flex min-h-[44px] items-center justify-center rounded-md px-4 py-2 text-sm font-semibold text-[color:var(--danger)] hover:bg-soi-100"
          >
            {t('profile.delete_account')}
          </a>
        </div>
        <p className="mt-2 text-[12px] text-ink-muted">{t('profile.pdpa_note')}</p>
      </section>

      {isError && error instanceof Error && (
        <p className="mt-6 text-sm text-[color:var(--danger)]">{error.message}</p>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  loading,
}: {
  label: string;
  value: number;
  loading: boolean;
}) {
  return (
    <article className="rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] p-3 text-center">
      {loading ? (
        <Skeleton width={36} height={24} />
      ) : (
        <div className="tabular text-2xl font-extrabold">{value}</div>
      )}
      <div className="mt-0.5 text-[12px] text-ink-muted">{label}</div>
    </article>
  );
}
